import { useAuthStore } from '@/stores/auth.store';
import { useRoleAccess } from './useRoleAccess';
import { useMemo } from 'react';

/**
 * usePermissionGuards — Centralized action-level RBAC hook
 * Checks fine-grained operational permissions for sensitive ERP operations.
 */
export function usePermissionGuards() {
  const user = useAuthStore((s) => s.user);
  const { hasPermission, isAdmin } = useRoleAccess();

  return useMemo(() => {
    const role = (user?.role || '').toUpperCase();

    // 1. Phê duyệt Báo Giá (Approve Quotation)
    const canApproveQuotation =
      isAdmin ||
      hasPermission('quotations', 'approve');

    // 2. Chốt Sổ Kỳ Đối Soát AR / AP (Reconciliation Confirmation)
    const canConfirmReconciliation =
      isAdmin ||
      hasPermission('reconciliations', 'approve') ||
      hasPermission('debts', 'approve') ||
      hasPermission('finance', 'approve');

    // 3. Quản lý / Phát / Hủy Lệnh Sản Xuất (Manage Production Orders)
    const canManageProductionOrders =
      isAdmin ||
      hasPermission('production-orders', 'update') ||
      hasPermission('production-orders', 'approve');

    // 4. Hủy Đơn Hàng (Cancel Sales Order)
    const canCancelOrder =
      isAdmin ||
      hasPermission('orders', 'delete') ||
      hasPermission('orders', 'approve');

    // 5. Xóa Vĩnh Viễn / Xóa Thùng Rác (Hard Delete)
    const canHardDelete =
      isAdmin ||
      hasPermission('trash', 'delete');

    return {
      user,
      role,
      isAdmin,
      canApproveQuotation,
      canConfirmReconciliation,
      canManageProductionOrders,
      canCancelOrder,
      canHardDelete,
      checkActionAllowed: (module: string, action: string) => {
        if (isAdmin) return true;
        if (action === 'approve') {
          if (module === 'quotations') return canApproveQuotation;
          if (module === 'reconciliations' || module === 'finance' || module === 'debts') return canConfirmReconciliation;
          if (module === 'production' || module === 'productionOrders' || module === 'production-orders') return canManageProductionOrders;
        }
        return hasPermission(module, action);
      }
    };
  }, [user, hasPermission, isAdmin]);
}
